import {
  ChangeEvent,
  ChangeEventHandler,
  FocusEventHandler,
  MouseEventHandler,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { EventCollectorProps } from '../../../hooks/eventCollector/types';
import { EyeClosedIcon, EyeOpenIcon } from 'components/icons';
import { spacesSanitizer } from '../../../utils/sanitizers';
import classes from 'classnames';
import styles from './Input.module.scss';
import TextInputEvent from '../../../hooks/eventCollector/events/TextInputEvent';
import useEventCollector from '../../../hooks/eventCollector/useEventCollector';

type InputType =
  | 'text'
  | 'password'
  | 'email'
  | 'tel'
  | 'number'
  | 'search'
  | 'url';

export type InputProps = {
  error?: boolean;
  touched?: boolean;
  label: string;
  dataTest?: string;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  onFocus?: FocusEventHandler<HTMLInputElement>;
  value: string;
  name: string;
  className?: string;
  inputClassName?: string;
  disabled?: boolean;
  type?: InputType;
  rightText?: string;
  rightTextBold?: boolean;
  rightTextClassName?: string;
  formatter?: (value: string) => string;
  allowSpaces?: boolean;
  pattern?: string;
};

export default function Input({
  error,
  name,
  className,
  inputClassName,
  label,
  dataTest,
  onChange,
  onBlur,
  onFocus,
  value,
  type = 'text',
  disabled = false,
  rightText,
  rightTextBold,
  rightTextClassName,
  formatter,
  eventCollector,
  allowSpaces = true,
  pattern,
}: InputProps & EventCollectorProps) {
  const { track } = useEventCollector();
  const isPasswordType = type === 'password';
  const [isPasswordHidden, setPasswordHidden] = useState(isPasswordType);
  const [focused, setFocused] = useState(false);

  const eventCollectorTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const handleFocus = useCallback<FocusEventHandler<HTMLInputElement>>(
    (e) => {
      onFocus?.(e);
      setFocused(true);
    },
    [onFocus],
  );

  const handleBlur = useCallback<FocusEventHandler<HTMLInputElement>>(
    (e) => {
      onBlur?.(e);
      setFocused(false);
    },
    [onBlur],
  );

  const formattedValue = useMemo(
    () => (formatter !== undefined && !focused ? formatter(value) : value),
    [formatter, focused, value],
  );

  const handlePasswordVisibility: MouseEventHandler = () => {
    setPasswordHidden((prev) => !prev);
  };

  const evaluateType = (type: InputType) => {
    switch (type) {
      case 'password':
        if (isPasswordHidden) {
          return 'password';
        } else {
          return 'text';
        }

      case 'number':
        return focused ? 'number' : 'text';

      default:
        return type;
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!allowSpaces) {
      e.target.value = spacesSanitizer(e.target.value);
    }
    // validate pattern if provided
    const regex = pattern && new RegExp(`^${pattern}$`);
    if (regex && !regex.test(e.target.value)) return;

    onChange?.(e);
    clearTimeout(eventCollectorTimeoutRef.current);
    eventCollectorTimeoutRef.current = setTimeout(() => {
      handleAnalytics(e.target.value);
    }, 500);
  };

  const handleAnalytics = (val: string | number) => {
    track(
      new TextInputEvent({
        value:
          type === 'password'
            ? '<not tracked>'
            : eventCollector?.contentOverride ?? val,
        description: eventCollector?.description ?? name,
      }),
    );
  };

  return (
    <div className={classes(styles.container, className)}>
      <input
        type={evaluateType(type)}
        data-test={dataTest}
        data-password={isPasswordType}
        name={name}
        id={name}
        value={formattedValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        className={classes(
          styles.input,
          error && styles.danger,
          inputClassName,
        )}
        placeholder=" " // NOTE: Used for detecting if input has value
        disabled={disabled}
        pattern={pattern}
      />
      {label && (
        <label className={classes(styles.label)} htmlFor={name}>
          {label}
        </label>
      )}
      {isPasswordType && (
        <>
          <button
            className={classes(styles.password, styles.hidden)}
            onClick={handlePasswordVisibility}
            type="button"
          >
            <EyeClosedIcon />
          </button>
          <button
            className={classes(styles.password, styles.visible)}
            onClick={handlePasswordVisibility}
            type="button"
          >
            <EyeOpenIcon />
          </button>
        </>
      )}
      {rightText !== undefined && (
        <div className={styles['right-text-wrapper']}>
          <div
            className={classes(
              styles['right-text'],
              rightTextBold ? styles['right-text-bold'] : undefined,
              rightTextClassName,
            )}
          >
            {rightText}
          </div>
        </div>
      )}
    </div>
  );
}
